import {
	getAppliedOfferByItems,
	getAppliedOfferByReceipt,
} from "../get-applied-offer";
import {
	getAmountWithinRange,
	getDiscountAmount,
} from "../get-discount-amount";
import { bigMath } from "../misc";
import {
	DiscountInput,
	OfferLike,
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import {
	$deepPopulateItemMetadata,
	$isOrderUnpaid,
	sortByAscendingSubtotal,
} from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyNonReceiptOfferToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	offer,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	offer: OfferLike;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	const { eligibleItems } = getAppliedOfferByItems(orderItems, offer);

	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		subtotal_addons: {
			...orderItem.subtotal_addons,
			offer: {
				amount: eligibleItems[orderItem._id.toString()] || 0,
				metadata: eligibleItems[orderItem._id.toString()] ? offer : undefined,
				is_custom_value: false,
			},
		},
	}));

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const itemEligibleAmount = eligibleItems[orderItem._id.toString()] || 0;
			const optionPriceRatioToItem = bigMath.div(
				orderItemOption.subtotal,
				orderItem.subtotal,
			);
			const optionEligibleAmount = bigMath.mul(
				optionPriceRatioToItem,
				itemEligibleAmount,
			);

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					offer: {
						amount: optionEligibleAmount,
						metadata: optionEligibleAmount ? offer : undefined,
						is_custom_value: false,
					},
				},
			};
		}),
	}));

	const offer_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.offer.amount),
		0,
	);

	return calculateOrder({ ...order, items: orderItems, subtotal, offer_amount })
		.$order;
};

export const applyNonReceiptOfferToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders,
	offer,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	offer: OfferLike;
	skipPaidCheck?: boolean;
}) => {
	return orders
		.filter((o) => (skipPaidCheck ? true : $isOrderUnpaid(o)))
		.map((order) => applyNonReceiptOfferToOrder({ order, offer }));
};

export const applyReceiptOfferToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	offer,
	dividedAmountFromGroup,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	offer: OfferLike;
	dividedAmountFromGroup?: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	const { offerAmount } = dividedAmountFromGroup
		? { offerAmount: dividedAmountFromGroup }
		: getAppliedOfferByReceipt(subtotal, offer);

	const originalOrder = new Map(
		orderItems.map((item, i) => [item._id.toString(), i]),
	);

	let remainingAmountForDivision = offerAmount;
	orderItems = orderItems.sort(sortByAscendingSubtotal).map((orderItem, i) => {
		const isThereAmountToDivide = remainingAmountForDivision > 0;
		const isThisLastItem = i === orderItems.length - 1;

		if (isThisLastItem) {
			let dividedOfferAmountForItem = remainingAmountForDivision;

			if (dividedOfferAmountForItem > (orderItem.subtotal || 0)) {
				dividedOfferAmountForItem = orderItem.subtotal || 0;
			}

			const subtotal_addons = {
				...orderItem.subtotal_addons,
				offer: {
					amount: dividedOfferAmountForItem,
					metadata: isThereAmountToDivide ? offer : undefined,
					is_custom_value: false,
				},
			};

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedOfferAmountForItem,
			);

			return { ...orderItem, subtotal_addons };
		}

		const itemPriceRatioToOrder = bigMath.div(orderItem.subtotal, subtotal);
		let dividedOfferAmountForItem = isThereAmountToDivide
			? bigMath.mul(offerAmount, itemPriceRatioToOrder)
			: 0;
		dividedOfferAmountForItem =
			dividedOfferAmountForItem > orderItem.subtotal
				? orderItem.subtotal
				: dividedOfferAmountForItem;

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			offer: {
				amount: dividedOfferAmountForItem,
				metadata: dividedOfferAmountForItem ? offer : undefined,
				is_custom_value: false,
			},
		};

		remainingAmountForDivision = bigMath.sub(
			remainingAmountForDivision,
			dividedOfferAmountForItem,
		);

		return { ...orderItem, subtotal_addons };
	});

	if (remainingAmountForDivision > 0) {
		orderItems = orderItems.map((orderItem) => {
			if (
				orderItem.subtotal_addons.offer.amount === orderItem.subtotal ||
				remainingAmountForDivision === 0
			) {
				return orderItem;
			}

			const subtotalDiff = bigMath.sub(
				orderItem.subtotal,
				orderItem.subtotal_addons.offer.amount,
			);
			const addedAmount =
				subtotalDiff > remainingAmountForDivision
					? remainingAmountForDivision
					: subtotalDiff;

			const subtotal_addons = {
				...orderItem.subtotal_addons,
				offer: {
					...orderItem.subtotal_addons.offer,
					amount: bigMath.add(
						orderItem.subtotal_addons.offer.amount,
						addedAmount,
					),
				},
			};

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				addedAmount,
			);

			return { ...orderItem, subtotal_addons };
		});
	}

	// Restore original item order
	orderItems = orderItems.sort(
		(a, b) =>
			(originalOrder.get(a._id.toString()) ?? 0) -
			(originalOrder.get(b._id.toString()) ?? 0),
	);

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const optionPriceRatioToItem = bigMath.div(
				orderItemOption.subtotal,
				orderItem.subtotal,
			);
			let amount = bigMath.mul(
				optionPriceRatioToItem,
				orderItem.subtotal_addons.discount.amount,
			);
			amount = getAmountWithinRange(0, amount, orderItemOption.subtotal);

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					offer: {
						amount,
						metadata: amount ? offer : undefined,
						is_custom_value: false,
					},
				},
			};
		}),
	}));

	const offer_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.offer.amount),
		0,
	);

	return calculateOrder({ ...order, items: orderItems, subtotal, offer_amount })
		.$order;
};

export const applyReceiptOfferToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders: allOrders,
	offer,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	offer: OfferLike;
	skipPaidCheck?: boolean;
}) => {
	const unpaidOrders = allOrders.filter((o) =>
		skipPaidCheck ? true : $isOrderUnpaid(o),
	);
	const ordersGroupSubtotal = unpaidOrders.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);
	const { offerAmount } = getAppliedOfferByReceipt(ordersGroupSubtotal, offer);

	// For 100% off offers, we want to distribute the full offer amount
	// For other offers, cap to not exceed the combined orders subtotal
	const isFullDiscount = offerAmount >= ordersGroupSubtotal;
	const targetOfferAmount = isFullDiscount
		? offerAmount
		: Math.min(offerAmount, ordersGroupSubtotal);
	let remainingAmountForDivision = targetOfferAmount;

	const updatedOrders = unpaidOrders
		.sort((a, b) => (a.subtotal || 0) - (b.subtotal || 0))
		.map((order, i) => {
			const isThereAmountToDivide = remainingAmountForDivision > 0;
			const isThisLastItem = i === unpaidOrders.length - 1;

			if (isThisLastItem) {
				// For the last item, use all remaining amount, but don't exceed order subtotal
				const finalAmount = Math.min(
					remainingAmountForDivision,
					order.subtotal || 0,
				);
				return applyReceiptOfferToOrder({
					order,
					offer,
					dividedAmountFromGroup: isThereAmountToDivide ? finalAmount : 0,
				});
			}

			const orderPriceRatioToGroup = bigMath.div(
				order.subtotal || 0,
				ordersGroupSubtotal,
			);
			let dividedOfferAmountForOrder = isThereAmountToDivide
				? bigMath.mul(targetOfferAmount, orderPriceRatioToGroup)
				: 0;

			// Ensure the divided amount doesn't exceed the order subtotal
			dividedOfferAmountForOrder = Math.min(
				dividedOfferAmountForOrder,
				order.subtotal || 0,
			);

			// Ensure we don't exceed the remaining amount to divide
			dividedOfferAmountForOrder = Math.min(
				dividedOfferAmountForOrder,
				remainingAmountForDivision,
			);

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedOfferAmountForOrder,
			);

			return applyReceiptOfferToOrder({
				order,
				offer,
				dividedAmountFromGroup: dividedOfferAmountForOrder,
			});
		});

	// If there's still remaining amount and this is a full discount, distribute it
	if (remainingAmountForDivision > 0 && isFullDiscount) {
		// Find orders that can still accept more offer amount
		const ordersWithRemainingCapacity = updatedOrders
			.map((order, index) => ({
				order,
				index,
				remainingCapacity: Math.max(
					0,
					(unpaidOrders[index].subtotal || 0) -
						calculateOrder(order).offer_amount,
				),
			}))
			.filter((item) => item.remainingCapacity > 0);

		// Distribute remaining amount among orders with capacity
		let remainingToDistribute = remainingAmountForDivision;
		for (const item of ordersWithRemainingCapacity) {
			if (remainingToDistribute <= 0) break;

			const amountToAdd = Math.min(
				remainingToDistribute,
				item.remainingCapacity,
			);
			const currentOrder = updatedOrders[item.index];

			// Recalculate the order with the additional offer amount
			const recalculatedOrder = applyReceiptOfferToOrder({
				order: unpaidOrders[item.index],
				offer,
				dividedAmountFromGroup:
					calculateOrder(currentOrder).offer_amount + amountToAdd,
			});

			updatedOrders[item.index] = recalculatedOrder;
			remainingToDistribute -= amountToAdd;
		}
	}

	return updatedOrders;
};

export const applyOfferWithCustomValueToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	offer,
	customValue,
	dividedAmountFromGroup,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	offer: OfferLike;
	customValue: DiscountInput;
	dividedAmountFromGroup?: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	const offerAmount =
		dividedAmountFromGroup ??
		getDiscountAmount(subtotal, customValue).discountAmount;

	const originalOrder = new Map(
		orderItems.map((item, i) => [item._id.toString(), i]),
	);

	let remainingAmountForDivision = offerAmount;
	orderItems = orderItems.sort(sortByAscendingSubtotal).map((orderItem, i) => {
		const isThereAmountToDivide = remainingAmountForDivision > 0;
		const isThisLastItem = i === orderItems.length - 1;

		if (isThisLastItem) {
			let dividedOfferAmountForItem = remainingAmountForDivision;

			if (dividedOfferAmountForItem > (orderItem.subtotal || 0)) {
				dividedOfferAmountForItem = orderItem.subtotal || 0;
			}

			const subtotal_addons = {
				...orderItem.subtotal_addons,
				offer: {
					amount: dividedOfferAmountForItem,
					metadata: isThereAmountToDivide ? offer : undefined,
					is_custom_value: isThereAmountToDivide,
				},
			};

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedOfferAmountForItem,
			);

			return { ...orderItem, subtotal_addons };
		}

		const itemPriceRatioToOrder = bigMath.div(orderItem.subtotal, subtotal);
		let dividedOfferAmountForItem = isThereAmountToDivide
			? bigMath.mul(offerAmount, itemPriceRatioToOrder)
			: 0;
		dividedOfferAmountForItem =
			dividedOfferAmountForItem > orderItem.subtotal
				? orderItem.subtotal
				: dividedOfferAmountForItem;

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			offer: {
				amount: dividedOfferAmountForItem,
				metadata: dividedOfferAmountForItem ? offer : undefined,
				is_custom_value: !!dividedOfferAmountForItem,
			},
		};

		remainingAmountForDivision = bigMath.sub(
			remainingAmountForDivision,
			dividedOfferAmountForItem,
		);

		return { ...orderItem, subtotal_addons };
	});

	if (remainingAmountForDivision > 0) {
		orderItems = orderItems.map((orderItem) => {
			if (
				orderItem.subtotal_addons.offer.amount === orderItem.subtotal ||
				remainingAmountForDivision === 0
			) {
				return orderItem;
			}

			const subtotalDiff = bigMath.sub(
				orderItem.subtotal,
				orderItem.subtotal_addons.offer.amount,
			);
			const addedAmount =
				subtotalDiff > remainingAmountForDivision
					? remainingAmountForDivision
					: subtotalDiff;

			const subtotal_addons = {
				...orderItem.subtotal_addons,
				offer: {
					...orderItem.subtotal_addons.offer,
					amount: bigMath.add(
						orderItem.subtotal_addons.offer.amount,
						addedAmount,
					),
				},
			};

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				addedAmount,
			);

			return { ...orderItem, subtotal_addons };
		});
	}

	// Restore original item order
	orderItems = orderItems.sort(
		(a, b) =>
			(originalOrder.get(a._id.toString()) ?? 0) -
			(originalOrder.get(b._id.toString()) ?? 0),
	);

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const optionPriceRatioToItem = bigMath.div(
				orderItemOption.subtotal,
				orderItem.subtotal,
			);
			let amount = bigMath.mul(
				optionPriceRatioToItem,
				orderItem.subtotal_addons.discount.amount,
			);
			amount = getAmountWithinRange(0, amount, orderItemOption.subtotal);

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					offer: {
						amount,
						metadata: amount ? offer : undefined,
						is_custom_value: !!amount,
					},
				},
			};
		}),
	}));

	const offer_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.offer.amount),
		0,
	);

	return calculateOrder({ ...order, items: orderItems, subtotal, offer_amount })
		.$order;
};

export const applyOfferWithCustomValueToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders: allOrders,
	offer,
	customValue,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	offer: OfferLike;
	customValue: DiscountInput;
	skipPaidCheck?: boolean;
}) => {
	const unpaidOrders = allOrders.filter((o) =>
		skipPaidCheck ? true : $isOrderUnpaid(o),
	);
	const ordersGroupSubtotal = unpaidOrders.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);
	const offerAmount = getDiscountAmount(
		ordersGroupSubtotal,
		customValue,
	).discountAmount;

	// For 100% off offers, we want to distribute the full offer amount
	// For other offers, cap to not exceed the combined orders subtotal
	const isFullDiscount = offerAmount >= ordersGroupSubtotal;
	const targetOfferAmount = isFullDiscount
		? offerAmount
		: Math.min(offerAmount, ordersGroupSubtotal);
	let remainingAmountForDivision = targetOfferAmount;

	const updatedOrders = unpaidOrders
		.sort((a, b) => (a.subtotal || 0) - (b.subtotal || 0))
		.map((order, i) => {
			const isThereAmountToDivide = remainingAmountForDivision > 0;
			const isThisLastItem = i === unpaidOrders.length - 1;

			if (isThisLastItem) {
				// For the last item, use all remaining amount, but don't exceed order subtotal
				const finalAmount = Math.min(
					remainingAmountForDivision,
					order.subtotal || 0,
				);
				return applyOfferWithCustomValueToOrder({
					order,
					offer,
					customValue,
					dividedAmountFromGroup: isThereAmountToDivide ? finalAmount : 0,
				});
			}

			const orderPriceRatioToGroup = bigMath.div(
				order.subtotal || 0,
				ordersGroupSubtotal,
			);
			let dividedOfferAmountForOrder = isThereAmountToDivide
				? bigMath.mul(targetOfferAmount, orderPriceRatioToGroup)
				: 0;

			// Ensure the divided amount doesn't exceed the order subtotal
			dividedOfferAmountForOrder = Math.min(
				dividedOfferAmountForOrder,
				order.subtotal || 0,
			);

			// Ensure we don't exceed the remaining amount to divide
			dividedOfferAmountForOrder = Math.min(
				dividedOfferAmountForOrder,
				remainingAmountForDivision,
			);

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedOfferAmountForOrder,
			);

			return applyOfferWithCustomValueToOrder({
				order,
				offer,
				customValue,
				dividedAmountFromGroup: dividedOfferAmountForOrder,
			});
		});

	// If there's still remaining amount and this is a full discount, distribute it
	if (remainingAmountForDivision > 0 && isFullDiscount) {
		// Find orders that can still accept more offer amount
		const ordersWithRemainingCapacity = updatedOrders
			.map((order, index) => ({
				order,
				index,
				remainingCapacity: Math.max(
					0,
					(unpaidOrders[index].subtotal || 0) -
						calculateOrder(order).offer_amount,
				),
			}))
			.filter((item) => item.remainingCapacity > 0);

		// Distribute remaining amount among orders with capacity
		let remainingToDistribute = remainingAmountForDivision;
		for (const item of ordersWithRemainingCapacity) {
			if (remainingToDistribute <= 0) break;

			const amountToAdd = Math.min(
				remainingToDistribute,
				item.remainingCapacity,
			);
			const currentOrder = updatedOrders[item.index];

			// Recalculate the order with the additional offer amount
			const recalculatedOrder = applyOfferWithCustomValueToOrder({
				order: unpaidOrders[item.index],
				offer,
				customValue,
				dividedAmountFromGroup:
					calculateOrder(currentOrder).offer_amount + amountToAdd,
			});

			updatedOrders[item.index] = recalculatedOrder;
			remainingToDistribute -= amountToAdd;
		}
	}

	return updatedOrders;
};
